import { useEffect, useState, type CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router";
import { Menu, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AdminNavigation } from "@/components/admin/admin-navigation";
import { CommandPalette } from "@/components/admin/command-palette";
import { Enrollment, LocalSignIn } from "@/components/admin/local-auth";
import { PageHeaderHostProvider } from "@/components/admin/page-header";
import { UserMenu } from "@/components/admin/user-menu";
import { ToastHost } from "@/components/admin/toast-host";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { adminConfig } from "@/lib/config";
import { adminApi, ApiError } from "@/lib/api";
import { adminSessionExpiredEvent } from "@/lib/admin-session";
import { queryKeys, useCurrentIdentityQuery } from "@/lib/queries";

export const ADMIN_ROOT_PATH = "/admin/";
export const ADMIN_SIDEBAR_STORAGE_KEY = "tb_admin_sidebar";
const appIcon = `${import.meta.env.BASE_URL}favicon.svg`;

function sidebarStartsExpanded() {
  try {
    return (
      typeof window === "undefined" ||
      window.localStorage.getItem(ADMIN_SIDEBAR_STORAGE_KEY) !== "collapsed"
    );
  } catch {
    return true;
  }
}

function Brand({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <div className="flex size-11 items-center justify-center">
        <span className="sr-only">LICENSE MANAGEMENT</span>
        <img src={appIcon} className="size-8" alt="" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2 px-1">
      <img src={appIcon} className="size-8 shrink-0" alt="" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-xs font-normal tracking-[-0.03em] text-ink">
          LICENSE MANAGEMENT
        </p>
        <p className="font-mono text-[10px] tracking-[0.14em] text-muted">
          {adminConfig.appVersion}
        </p>
      </div>
    </div>
  );
}

function SearchLauncher({
  expanded,
  onOpen,
}: {
  expanded: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const label = t("commandPalette.open", "Search");

  return (
    <Button
      type="button"
      variant="outline"
      size={expanded ? "sm" : "icon"}
      className={expanded ? "w-full justify-between gap-3 text-muted hover:text-ink" : undefined}
      aria-label={label}
      aria-keyshortcuts="Meta+K Control+K"
      onClick={onOpen}
    >
      <span className={expanded ? "flex items-center gap-2" : undefined}>
        <Search className="size-4" aria-hidden="true" />
        {expanded ? <span>{label}</span> : null}
      </span>
      {expanded ? (
        <kbd
          className="hidden rounded-sm border border-line px-1.5 py-0.5 font-mono text-[10px] sm:inline-flex"
          aria-hidden="true"
        >
          ⌘K
        </kbd>
      ) : null}
    </Button>
  );
}

function SidebarStatus({
  visible,
  sessionUnavailable,
}: {
  visible: boolean;
  sessionUnavailable: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={
        visible ? "mb-3 flex min-w-0 flex-wrap gap-2 px-3" : "sr-only"
      }
    >
      {adminConfig.demoMode && (
        <Badge variant="warning">{t("common.demoData", "Demo data")}</Badge>
      )}
      {sessionUnavailable ? (
        <Badge variant="danger" indicator>
          {t("nav.sessionUnavailable", "Session unavailable")}
        </Badge>
      ) : null}
    </div>
  );
}

function SessionLoading() {
  const { t } = useTranslation();
  return <main className="flex min-h-dvh items-center justify-center bg-canvas px-4 text-ink"><section className="w-full max-w-md rounded-sm border border-line bg-panel p-6" role="status" aria-live="polite"><p className="text-sm text-muted">{t("nav.sessionChecking", "Checking your session…")}</p></section></main>;
}

function SessionUnavailable({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return <main className="flex min-h-dvh items-center justify-center bg-canvas px-4 text-ink"><section className="w-full max-w-md rounded-sm border border-line bg-panel p-6" role="alert"><h1 className="text-xl font-normal tracking-[-0.04em]">{t("nav.sessionUnavailableTitle", "Admin session is unavailable")}</h1><p className="mt-2 text-sm text-muted">{t("nav.sessionUnavailableDescription", "The Admin API could not be reached. Check the connection and try again.")}</p><Button className="mt-5" variant="primary" onClick={onRetry}>{t("common.retry", "Retry")}</Button></section></main>;
}

export function AppShell() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(sidebarStartsExpanded);
  const [pageHeaderHost, setPageHeaderHost] = useState<HTMLDivElement | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const identityQuery = useCurrentIdentityQuery();
  const identity = identityQuery.data;
  const sessionLabel = identityQuery.isPending
    ? t("nav.sessionLoading", "Loading API session")
    : identityQuery.isError
      ? t("nav.sessionUnavailable", "Session unavailable")
      : t("nav.sessionReady", "API session ready");
  const unauthorized = !adminConfig.demoMode && (sessionExpired || (identityQuery.error instanceof ApiError && identityQuery.error.status === 401));

  useEffect(() => {
    if (unauthorized) {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] === "admin" && query.queryKey[1] !== queryKeys.identity[1],
      });
    }
  }, [queryClient, unauthorized]);

  useEffect(() => {
    const handleSessionExpired = () => { setSessionExpired(true); void queryClient.invalidateQueries({ queryKey: queryKeys.identity }); };
    window.addEventListener(adminSessionExpiredEvent, handleSessionExpired);
    return () => window.removeEventListener(adminSessionExpiredEvent, handleSessionExpired);
  }, [queryClient]);

  useEffect(() => {
    function onSearchShortcut(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k" || event.altKey || event.shiftKey) return;
      event.preventDefault();
      setPaletteOpen((open) => !open);
    }
    window.addEventListener("keydown", onSearchShortcut);
    return () => window.removeEventListener("keydown", onSearchShortcut);
  }, []);

  if (!adminConfig.demoMode && identityQuery.isPending) return <SessionLoading />;

  if (unauthorized) {
    const complete = () => { setSessionExpired(false); void queryClient.invalidateQueries({ queryKey: queryKeys.identity }); };
    return location.pathname === "/admin/enroll"
      ? <Enrollment onComplete={() => window.location.assign(ADMIN_ROOT_PATH)} />
      : <LocalSignIn onComplete={complete} />;
  }

  if (!adminConfig.demoMode && identityQuery.isError) {
    return <SessionUnavailable onRetry={() => void identityQuery.refetch()} />;
  }

  if (!adminConfig.demoMode && location.pathname === "/admin/enroll") {
    return <Navigate to="/admin/usage" replace />;
  }

  function toggleSidebar() {
    setSidebarExpanded((expanded) => {
      const next = !expanded;
      try {
        if (next) {
          window.localStorage.setItem(ADMIN_SIDEBAR_STORAGE_KEY, "expanded");
        } else {
          window.localStorage.setItem(ADMIN_SIDEBAR_STORAGE_KEY, "collapsed");
        }
      } catch {
        // Keep the in-memory preference when browser storage is unavailable.
      }
      return next;
    });
  }

  async function signOut() {
    setSigningOut(true);
    setSignOutFailed(false);
    try {
      await adminApi.logout();
      queryClient.clear();
      window.location.assign(ADMIN_ROOT_PATH);
    } catch {
      setSignOutFailed(true);
    } finally {
      setSigningOut(false);
    }
  }

  const shellStyle = {
    "--admin-sidebar-width": sidebarExpanded ? "240px" : "66px",
  } as CSSProperties;
  const navigationToggle = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={
        sidebarExpanded
          ? t("nav.collapseNavigation", "Collapse navigation")
          : t("nav.expandNavigation", "Expand navigation")
      }
      aria-expanded={sidebarExpanded}
      aria-controls="admin-desktop-navigation"
      onClick={toggleSidebar}
    >
      {sidebarExpanded ? (
        <PanelLeftClose className="size-4" aria-hidden="true" />
      ) : (
        <PanelLeftOpen className="size-4" aria-hidden="true" />
      )}
    </Button>
  );

  return (
    <PageHeaderHostProvider host={pageHeaderHost}>
      <div
        className="min-h-dvh bg-canvas text-ink xl:h-dvh xl:overflow-hidden"
        style={shellStyle}
      >
        <aside className="admin-sidebar fixed inset-y-0 left-0 z-30 hidden w-(--admin-sidebar-width) border-r border-line bg-brand-dark text-brand-on transition-[width] duration-200 ease-out motion-reduce:transition-none xl:block">
          <div
            className={
              sidebarExpanded
                ? "flex h-full flex-col px-3 py-4"
                : "flex h-full flex-col items-center px-2 py-4"
            }
          >
            <div className={sidebarExpanded ? "flex items-center justify-between gap-1" : "flex flex-col items-center gap-2"}>
              <Brand compact={!sidebarExpanded} />
              <Tooltip>
                <TooltipTrigger>{navigationToggle}</TooltipTrigger>
                <TooltipContent side="right">
                  {sidebarExpanded
                    ? t("nav.collapseNavigation", "Collapse navigation")
                    : t("nav.expandNavigation", "Expand navigation")}
                </TooltipContent>
              </Tooltip>
            </div>
            <div
              className={
                sidebarExpanded
                  ? "mt-4 w-full"
                  : "mt-4 flex w-full justify-center"
              }
            >
              <SearchLauncher
                expanded={sidebarExpanded}
                onOpen={() => setPaletteOpen(true)}
              />
            </div>
            <div
              id="admin-desktop-navigation"
              className="console-scrollbar mt-5 min-h-0 w-full flex-1 overflow-y-auto pb-3"
            >
              <AdminNavigation
                variant={sidebarExpanded ? "list" : "rail"}
                roles={identity?.roles}
              />
            </div>
            <div
              className={
                sidebarExpanded
                  ? "mt-auto w-full border-t border-line pt-2"
                  : "mt-auto flex w-full flex-col items-center border-t border-line pt-2"
              }
            >
              <SidebarStatus
                visible={sidebarExpanded}
                sessionUnavailable={identityQuery.isError}
              />
              <UserMenu
                identity={identity}
                loading={identityQuery.isPending}
                sessionError={identityQuery.isError}
                sessionLabel={sessionLabel}
                signingOut={signingOut}
                sidebar
                collapsed={!sidebarExpanded}
                onSignOut={!adminConfig.demoMode && identity ? signOut : undefined}
              />
            </div>
          </div>
        </aside>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="admin-sidebar border-line bg-brand-dark px-3 py-5 text-brand-on xl:hidden">
            <SheetTitle className="sr-only">{t("nav.workspace", "Navigation")}</SheetTitle>
            <div className="flex items-center justify-between">
              <Brand />
              <SheetClose>
                <Button variant="ghost" size="icon" aria-label={t("nav.closeNavigation", "Close navigation")}>
                  <X className="size-4" aria-hidden="true" />
                </Button>
              </SheetClose>
            </div>
            <div className="mt-4 px-1">
              <SearchLauncher
                expanded
                onOpen={() => {
                  setMobileOpen(false);
                  setPaletteOpen(true);
                }}
              />
            </div>
            <div className="console-scrollbar mt-5 min-h-0 flex-1 overflow-y-auto px-1">
              <AdminNavigation variant="list" onNavigate={() => setMobileOpen(false)} roles={identity?.roles} />
            </div>
            <div className="mt-auto border-t border-line px-1 pt-2">
              <SidebarStatus
                visible
                sessionUnavailable={identityQuery.isError}
              />
              <UserMenu
                identity={identity}
                loading={identityQuery.isPending}
                sessionError={identityQuery.isError}
                sessionLabel={sessionLabel}
                signingOut={signingOut}
                sidebar
                onSignOut={!adminConfig.demoMode && identity ? signOut : undefined}
              />
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex min-h-dvh flex-col transition-[padding] duration-200 ease-out motion-reduce:transition-none xl:h-full xl:min-h-0 xl:pl-(--admin-sidebar-width)">
          <div className="min-w-0 border-b border-line px-4 pt-5 sm:px-6 xl:px-7">
            <div className="flex min-w-0 items-start gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="mt-0.5 xl:hidden"
                aria-label={t("nav.openNavigation", "Open navigation")}
                onClick={() => setMobileOpen(true)}
              >
                <Menu className="size-5" aria-hidden="true" />
              </Button>
              <div ref={setPageHeaderHost} className="min-w-0 flex-1" />
            </div>
          </div>

          <main className="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-6 xl:overflow-y-auto xl:px-7">
            {signOutFailed && <p className="mb-3 border border-danger/35 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">{t("auth.signOutError", "Sign out failed. Try again.")}</p>}
            <Outlet />
            <ToastHost />
          </main>
          <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </div>
      </div>
    </PageHeaderHostProvider>
  );
}
